/**
 * CustomerLayout - Nav for customer portal: Dashboard, Slides, Reports, Organization.
 */
import { Link, Outlet, useLocation } from "react-router-dom";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { navItemStyle, navItemHover, navItemActive } from "./ui/NavStyles";
import { authedGet } from "./api/customerAuthedFetch";
import {
  fetchCustomerNotices,
  overlayFingerprint,
  overlayStorageKey,
} from "./api/noticesApi";
import { formatNoticeLocal } from "./components/noticeTheme";
import NoticesOverlay from "./components/NoticesOverlay";
import SlidesMenu from "./components/SlidesMenu";
import OrganizationMenu from "./components/OrganizationMenu";
import PortalTourProvider from "./components/PortalTour";
import {
  CustomerRolesContext,
  customerRolesContextValue,
} from "./CustomerRolesContext";
import {
  ROLE_BILLING,
  normalizeCustomerRoles,
} from "./customerRoles";
import {
  clearImpersonationSession,
  getImpersonationClaims,
  hasImpersonationSession,
} from "./impersonationSession";

export const CustomerNoticesContext = createContext({
  blocking: false,
  notices: [],
});

const NOTICES_POLL_MS = 30000;

export default function CustomerLayout({ user, signOut }) {
  const location = useLocation();
  const [authEmail, setAuthEmail] = useState("Unknown user");
  const [authUsername, setAuthUsername] = useState("");
  const [authTime, setAuthTime] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [customerRoles, setCustomerRoles] = useState([]);
  const [rolesReady, setRolesReady] = useState(false);
  const [noticesPayload, setNoticesPayload] = useState({
    items: [],
    blocking: false,
    block_ends_at: null,
  });
  const [noticesReady, setNoticesReady] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [impersonation, setImpersonation] = useState(() =>
    hasImpersonationSession() ? getImpersonationClaims() : null
  );

  useEffect(() => {
    let mounted = true;
    async function loadAuth() {
      try {
        const claims = getImpersonationClaims();
        if (claims) {
          if (mounted) {
            setImpersonation(claims);
            setAuthEmail(claims.email || claims.impersonator_username || "Super Admin");
            setAuthUsername(
              claims.impersonator_username ||
                claims["cognito:username"] ||
                claims.email ||
                ""
            );
            setAuthTime(String(claims.iat || claims.exp || ""));
          }
          return;
        }
        setImpersonation(null);
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload || {};
        if (mounted) {
          setAuthEmail(payload.email || user?.username || "Unknown user");
          setAuthUsername(
            payload["cognito:username"] || user?.username || payload.email || ""
          );
          setAuthTime(String(payload.auth_time || payload.iat || ""));
        }
      } catch (err) {
        console.error("Failed to load auth session", err);
      }
    }
    loadAuth();
    return () => { mounted = false; };
  }, [user]);

  const refreshNotices = useCallback(async () => {
    try {
      const data = await fetchCustomerNotices();
      setNoticesPayload({
        items: data.items || [],
        blocking: Boolean(data.blocking),
        block_ends_at: data.block_ends_at || null,
      });
    } catch (err) {
      console.error("Failed to load notices", err);
    } finally {
      setNoticesReady(true);
    }
  }, []);

  useEffect(() => {
    refreshNotices();
    const id = setInterval(refreshNotices, NOTICES_POLL_MS);
    function onFocus() {
      refreshNotices();
    }
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshNotices]);

  useEffect(() => {
    const active = (noticesPayload.items || []).filter((n) => n.section === "active");
    if (!active.length) {
      setShowOverlay(false);
      return;
    }
    const fp = overlayFingerprint(authTime, active);
    try {
      if (sessionStorage.getItem(overlayStorageKey()) === fp) {
        setShowOverlay(false);
        return;
      }
    } catch {
      // ignore storage errors
    }
    setShowOverlay(true);
  }, [noticesPayload.items, authTime]);

  const blocking = Boolean(noticesPayload.blocking);
  const allowedWhenBlocked =
    location.pathname === "/notifications" ||
    location.pathname === "/account" ||
    location.pathname === "/organization/software";

  useEffect(() => {
    let mounted = true;
    async function loadProfile() {
      try {
        const profile = await authedGet("/customer/profile");
        if (mounted) {
          setCompanyName(profile.official_name || profile.display_name || "");
          setCustomerRoles(normalizeCustomerRoles(profile.roles));
        }
      } catch (err) {
        console.error("Failed to load customer profile", err);
        if (mounted) setCustomerRoles([]);
      } finally {
        if (mounted) setRolesReady(true);
      }
    }
    loadProfile();
    return () => { mounted = false; };
  }, []);

  const rolesContextValue = useMemo(
    () => customerRolesContextValue(customerRoles, rolesReady),
    [customerRoles, rolesReady]
  );

  function dismissOverlay() {
    const active = (noticesPayload.items || []).filter((n) => n.section === "active");
    try {
      sessionStorage.setItem(overlayStorageKey(), overlayFingerprint(authTime, active));
    } catch {
      // ignore
    }
    setShowOverlay(false);
  }

  const navLink = (to, label, tourId) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + "/");
    return (
      <Link
        to={to}
        data-tour={tourId}
        style={{ ...navItemStyle, ...(isActive ? navItemActive : {}) }}
        onMouseEnter={(e) => { if (!isActive) Object.assign(e.target.style, navItemHover); }}
        onMouseLeave={(e) => { if (!isActive) Object.assign(e.target.style, navItemStyle); }}
      >
        {label}
      </Link>
    );
  };

  const initials = authEmail
    .split("@")[0]
    .split(/[.\-_]/)
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  const visibleCount = (noticesPayload.items || []).filter((n) => n.section === "active").length;
  const activeItems = (noticesPayload.items || []).filter((n) => n.section === "active");
  const tourUserKey = authUsername || authEmail;

  const canSeeReports = rolesReady && rolesContextValue.hasAnyRole([ROLE_BILLING]);

  return (
    <CustomerRolesContext.Provider value={rolesContextValue}>
    <CustomerNoticesContext.Provider
      value={{ blocking, notices: noticesPayload.items || [] }}
    >
      <PortalTourProvider
        userKey={tourUserKey}
        blocking={blocking}
        noticesReady={noticesReady}
        noticesOverlayOpen={showOverlay}
      >
        <div>
          <div style={styles.earlyPreviewBar} role="status">
            <span style={styles.earlyPreviewBadge}>Early Preview</span>
            <span>
              This customer portal is in early preview. Features and layout may
              change.
            </span>
          </div>
          {impersonation ? (
            <div style={styles.impersonationBar} role="status">
              <span>
                Impersonating{" "}
                <strong>{companyName || impersonation["custom:companyId"]}</strong>
                {" "}as{" "}
                <strong>
                  {impersonation.impersonator_username ||
                    impersonation["cognito:username"] ||
                    authEmail}
                </strong>
                {" "}(Super Admin session — not a customer user)
              </span>
              <button
                type="button"
                style={styles.impersonationEndBtn}
                onClick={() => {
                  if (typeof signOut === "function") signOut();
                  else {
                    clearImpersonationSession();
                    window.close();
                    window.location.replace("about:blank");
                  }
                }}
              >
                End session
              </button>
            </div>
          ) : null}
          <header style={styles.header}>
            <nav style={styles.nav}>
              {!blocking && navLink("/", "Dashboard", "nav-dashboard")}
              {!blocking && <SlidesMenu />}
              {!blocking && canSeeReports && navLink("/reports", "Reports", "nav-reports")}
              {!blocking && <OrganizationMenu />}
              <div style={{ flex: 1 }} />
              {companyName && (
                <div style={styles.companyName}>{companyName}</div>
              )}
              <Link
                to="/notifications"
                title="Notifications"
                data-tour="nav-notices"
                style={{
                  ...styles.bell,
                  ...(location.pathname === "/notifications" ? styles.bellActive : {}),
                }}
              >
                <span aria-hidden>🔔</span>
                {visibleCount > 0 ? <span style={styles.badge}>{visibleCount}</span> : null}
              </Link>
              <div style={styles.accountBox} data-tour="nav-account">
                <Link
                  to="/account"
                  title="Account"
                  style={{
                    ...styles.accountLink,
                    ...(location.pathname === "/account" ? styles.accountLinkActive : {}),
                  }}
                >
                  <div style={styles.avatar}>{initials}</div>
                  <div style={styles.accountInfo}>
                    <div style={styles.email}>{authEmail}</div>
                    <div style={styles.accountHint}>
                      {impersonation ? "Impersonation" : "Account"}
                    </div>
                  </div>
                </Link>
                <button onClick={signOut} style={styles.signOutButton}>
                  {impersonation ? "End session" : "Sign out"}
                </button>
              </div>
            </nav>
          </header>
          {blocking ? (
            <div style={styles.blockBar}>
              Portal maintenance is in progress until{" "}
              {formatNoticeLocal(noticesPayload.block_ends_at)}. Notifications and
              Sign out remain available.
            </div>
          ) : null}
          <main style={{ padding: 16 }}>
            {blocking && !allowedWhenBlocked ? (
              <div style={styles.blockCard}>
                <h1 style={{ marginTop: 0, fontSize: 20 }}>Portal unavailable</h1>
                <p>
                  Customer portal maintenance is active until{" "}
                  {formatNoticeLocal(noticesPayload.block_ends_at)}. Browser slide
                  upload is disabled. The Windows upload agent can continue.
                </p>
                <p style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <Link to="/notifications">View notifications</Link>
                  <Link to="/organization/software">Software Downloads</Link>
                </p>
              </div>
            ) : (
              <Outlet context={{ blocking, notices: noticesPayload.items || [] }} />
            )}
          </main>
          {showOverlay ? (
            <NoticesOverlay
              items={activeItems}
              blocking={blocking}
              blockEndsAt={noticesPayload.block_ends_at}
              onDismiss={dismissOverlay}
            />
          ) : null}
        </div>
      </PortalTourProvider>
    </CustomerNoticesContext.Provider>
    </CustomerRolesContext.Provider>
  );
}

const styles = {
  earlyPreviewBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: "12px 18px",
    background: "#fef2f2",
    borderBottom: "2px solid #f87171",
    color: "#991b1b",
    fontSize: 15,
    fontWeight: 500,
  },
  earlyPreviewBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 5,
    background: "#dc2626",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  impersonationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "10px 16px",
    background: "#fff7ed",
    borderBottom: "2px solid #f59e0b",
    color: "#9a3412",
    fontSize: 13,
    fontWeight: 500,
  },
  impersonationEndBtn: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #c2410c",
    background: "#fff",
    color: "#9a3412",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  header: {
    padding: "10px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  companyName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    padding: "0 16px",
    marginRight: 12,
    borderRight: "1px solid #e5e7eb",
    letterSpacing: "0.02em",
  },
  bell: {
    position: "relative",
    textDecoration: "none",
    fontSize: 18,
    padding: "6px 8px",
    borderRadius: 8,
    color: "#111827",
  },
  bellActive: {
    outline: "2px solid #2563eb",
    outlineOffset: 1,
  },
  badge: {
    position: "absolute",
    top: 0,
    right: 0,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    background: "#dc2626",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 4px",
  },
  accountBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#ffffff",
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#2563eb",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
  },
  accountLink: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none",
    color: "inherit",
    borderRadius: 6,
    padding: 2,
  },
  accountLinkActive: {
    outline: "2px solid #2563eb",
    outlineOffset: 1,
  },
  accountInfo: { lineHeight: 1.2 },
  email: { fontSize: 13, fontWeight: 500 },
  accountHint: { fontSize: 11, color: "#6b7280" },
  signOutButton: {
    marginLeft: 8,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #dc2626",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: 12,
    cursor: "pointer",
  },
  blockBar: {
    background: "#7f1d1d",
    color: "#fff",
    padding: "8px 16px",
    fontSize: 13,
  },
  blockCard: {
    maxWidth: 640,
    padding: 20,
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fef2f2",
  },
};
